import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authApi } from '../../api/facades';
import { AuthenticationService, AUTH_KEYS } from '../../api';

const useQueryParams = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const query = useQueryParams();

  const token = query.get('token') || '';
  const email = query.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !email) {
      toast.error('Invalid or missing reset link.');
      return;
    }
    if (!password || password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.resetPassword({
        token,
        email,
        password,
        password_confirmation: confirm,
      });
      const data = res.data?.data;
      if (data?.token) {
        localStorage.setItem(AUTH_KEYS.accessToken, data.token);
      }
      if (data?.user) {
        localStorage.setItem(AUTH_KEYS.user, JSON.stringify(data.user));
      }
      toast.success('Password updated. Redirecting to dashboard...');
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      toast.error(err?.body?.message || err?.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8">
        <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
          Reset your password
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          Enter a new password for <span className="font-semibold">{email || 'your account'}</span>.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              New password
            </label>
            <input
              type="password"
              className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              Confirm password
            </label>
            <input
              type="password"
              className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 mt-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-60"
          >
            {loading ? 'Saving...' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

